import React, { useRef, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { AgGridReact } from "ag-grid-react";
import { Icon } from "@iconify/react";
import { GridApi } from "ag-grid-community";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import {
  useCreateBlackOutDay,
  useUpdateBlackOutDay,
} from "../../queries/transactionV2";
import { useEmployees } from "../../queries";
import { useGetDesignatedPersons } from "../../queries/designatedPerson";
import { Select } from "../../components/shared/Select";
import SearchInput from "../../components/shared/SearchInput";
import GenericTableHeader from "../../shared/GenericTableHeader";

type AddOrEditBlackOutDateProps = {
  mode: "Edit" | "Add";
  data?: any;
  onClose: () => void;
};

function AddOrEditBlackOutDate(props: AddOrEditBlackOutDateProps) {
  const { data, mode, onClose } = props;

  const { data: _getAllDesignatedPersonsList } = useGetDesignatedPersons();

  const gridApi = useRef<GridApi | any>(null);

  // State for affected employees
  const [affectedEmployees, setAffectedEmployees] = useState<any[]>(
    data?.affectedEmployees || []
  );

  useEffect(() => {
    if (data?.designatedPersonId && _getAllDesignatedPersonsList) {
      const selectedDP = _getAllDesignatedPersonsList.find(
        (item: any) => item.id === data.designatedPersonId
      );
      if (selectedDP) {
        setAffectedEmployees(selectedDP.employeesList || []);
      }
    }
  }, [data?.designatedPersonId, _getAllDesignatedPersonsList]);

  // AgGrid columns for affected employees
  const employeeColDefs = [
    { headerName: "Name", field: "name", flex: 1 },
    { headerName: "Email", field: "email", flex: 1 },
  ];

  const formatDate = (date: any) => {
    if (!date) return "";
    const d = new Date(date);
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const initialValues = {
    startDate: data?.startDate ? formatDate(data.startDate) : "",
    endDate: data?.endDate ? formatDate(data.endDate) : "",
    reason: data?.reason || "",
    notes: data?.notes || "",
    filters: data?.filters || {},
    designatedPersonId: data?.designatedPersonId || "",
  };

  const validationSchema = Yup.object().shape({
    startDate: Yup.date().required("Start date is required"),
    endDate: Yup.date()
      .required("End date is required")
      .min(Yup.ref("startDate"), "End date must be after start date"),
    reason: Yup.string().required("Reason is required"),
    notes: Yup.string(),
  });

  const { mutate: addBlackOutDate, isLoading: isAddLoading } =
    useCreateBlackOutDay();
  const { mutate: editBlackOutDate, isLoading: isEditLoading } =
    useUpdateBlackOutDay();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      if (affectedEmployees.length === 0) {
        toast("Please select at least one employee", { type: "error" });
        return;
      }
      const payload = { ...values, affectedEmployees };
      if (mode === "Add") {
        addBlackOutDate(payload, {
          onSuccess: () => {
            toast("Blackout date added successfully!", { type: "success" });
            onClose();
          },
          onError: (err: any) => {
            toast(err.response.data.reason, { type: "error", autoClose: 2000 });
          },
        });
      } else {
        editBlackOutDate(
          { id: data.id, ...payload },
          {
            onSuccess: () => {
              toast("Blackout date edited successfully!", { type: "success" });
              onClose();
            },
            onError: (err: any) => {
              toast(err.response.data.reason, {
                type: "error",
                autoClose: 2000,
              });
            },
          }
        );
      }
    },
  });

  const onGridReady = (params: any) => {
    gridApi.current = params;
  };

  return (
    <Box className="text-lg font-medium border-b py-7">
      <h6 className="flex justify-between pb-4 border-b px-7">
        {mode === "Edit" ? "Edit Blackout Date" : "Add Blackout Date"}
        <span onClick={onClose} className="cursor-pointer">
          ✕
        </span>
      </h6>
      <VStack className="w-full gap-4 pt-4 px-7">
        <HStack className="gap-4 ">
          <div className="flex-1">
            <VStack>
              <Label className="required">Start Date</Label>
              <Input type="date" {...formik.getFieldProps("startDate")} />
              {formik.touched.startDate && formik.errors.startDate && (
                <Error text={formik.errors.startDate as string} />
              )}
            </VStack>
          </div>
          <div className="flex-1">
            <VStack>
              <Label className="required">End Date</Label>
              <Input type="date" {...formik.getFieldProps("endDate")} />
              {formik.touched.endDate && formik.errors.endDate && (
                <Error text={formik.errors.endDate as string} />
              )}
            </VStack>
          </div>
        </HStack>
        <HStack className="gap-4">
          <div className="flex-1">
            <VStack>
              <Label className="required">Reason</Label>
              <TextArea {...formik.getFieldProps("reason")} />
            </VStack>
            {formik.touched.reason && formik.errors.reason && (
              <Error text={formik.errors.reason as string} />
            )}
          </div>
          <div className="flex-1">
            <VStack className="">
              <Label>Notes</Label>
              <TextArea {...formik.getFieldProps("notes")} />
            </VStack>
            {formik.touched.notes && formik.errors.notes && (
              <Error text={formik.errors.notes as string} />
            )}
          </div>
        </HStack>
        <HStack className="gap-4 ">
          <div className="flex-1">
            <Label>Select Designated Person List</Label>
            <Select
              options={_getAllDesignatedPersonsList || []}
              textGetter={(item: any) => item.name}
              valueGetter={(item: any) => item.id}
              value={formik.values.designatedPersonId}
              placeholder="Select Designated Person"
              onChange={(value) => {
                const selectedDesignatedPerson =
                  _getAllDesignatedPersonsList?.find(
                    (item: any) => item.id === value.target.value
                  );
                if (selectedDesignatedPerson) {
                  formik.setFieldValue(
                    "designatedPersonId",
                    selectedDesignatedPerson.id
                  );
                  setAffectedEmployees(
                    selectedDesignatedPerson.employeesList || []
                  );
                }
              }}
            />
          </div>
          <div className="flex-1"></div>
        </HStack>
        <VStack className="w-full gap-2 pt-6">
          <HStack className="justify-between gap-2">
            <HStack className="items-center gap-2">
              <GenericTableHeader
                heading={"Affected Employees"}
                subHeading={""}
                customClassName="flex flex-col px-0 lg:justify-start"
                additionalInfo={<>{`${affectedEmployees?.length} employees`}</>}
              ></GenericTableHeader>
            </HStack>
            <HStack>
              <HStack className="items-center gap-4 ">
                <SearchInput
                  className=""
                  placeholder="Search"
                  onChange={(e: any) => {
                    if (gridApi.current && gridApi.current.api) {
                      gridApi.current?.api.setQuickFilter(e.target.value);
                    }
                  }}
                />
              </HStack>
            </HStack>
          </HStack>
          <Box className="w-full h-48 mt-2 ag-theme-material">
            <AgGridReact
              rowData={affectedEmployees}
              columnDefs={employeeColDefs}
              onGridReady={onGridReady}
              suppressRowTransform={true}
              animateRows={true}
              suppressRowClickSelection
              suppressMenuHide={true}
              rowClass={
                "border-t border-dashed cursor-pointer hover:bg-slate-50 "
              }
              overlayNoRowsTemplate={
                '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
              }
            ></AgGridReact>
          </Box>
        </VStack>
        <HStack className="justify-between pt-6">
          <ButtonPrimary1 onClick={onClose}>Cancel</ButtonPrimary1>
          <ButtonPrimary
            type="submit"
            onClick={() => formik.handleSubmit()}
            loading={isAddLoading || isEditLoading}
            disabled={affectedEmployees.length === 0}
          >
            Save
          </ButtonPrimary>
        </HStack>
      </VStack>
    </Box>
  );
}

export default AddOrEditBlackOutDate;
